import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fileUrl } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UploadSimple, CircleNotch, Trash, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export function usePhotos() {
  const [photos, setPhotos] = useState([]);
  const load = useCallback(() => api.get("/marketing/photos").then((r) => setPhotos(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return [photos, load];
}

export function PhotoUploadButton({ onUploaded, testid = "mk-upload-photos-btn", label = "Carregar fotos da obra", variant = "outline" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(0);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(files.length);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post("/marketing/upload?kind=photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) { toast.error(`${f.name}: ${apiError(err)}`); }
      setBusy((b) => b - 1);
    }
    if (ok) toast.success(`${ok} foto(s) carregada(s)`);
    onUploaded?.();
  };
  return (
    <>
      <Button variant={variant} className="rounded-full" onClick={() => ref.current?.click()} disabled={busy > 0} data-testid={testid}>
        {busy > 0 ? <><CircleNotch className="mr-1.5 animate-spin" /> A carregar ({busy})</> : <><UploadSimple size={16} className="mr-1.5" /> {label}</>}
      </Button>
      <input ref={ref} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="mk-photo-input" />
    </>
  );
}

export function PhotoGrid({ photos, selected, onSelect, onDelete }) {
  if (!photos.length) return <p className="py-6 text-center text-sm text-muted-foreground" data-testid="mk-photos-empty">Ainda não carregou fotos das obras.</p>;
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {photos.map((p) => (
        <div key={p.id} className={`group relative aspect-square overflow-hidden rounded-lg border-2 ${selected === p.id ? "border-primary" : "border-transparent"}`}
          data-testid={`mk-photo-${p.id}`}>
          <button type="button" className="h-full w-full" onClick={() => onSelect?.(p.id)}>
            <img src={fileUrl(p.id)} alt="" loading="lazy" className="h-full w-full object-cover" />
          </button>
          {selected === p.id && <span className="absolute left-1 top-1 rounded-full bg-primary p-1 text-white"><Check size={12} weight="bold" /></span>}
          {onDelete && (
            <button type="button" onClick={() => onDelete(p)} data-testid={`mk-photo-delete-${p.id}`}
              className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
              <Trash size={12} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function PhotoLibraryDialog({ open, onClose, onChanged }) {
  const [photos, reload] = usePhotos();
  useEffect(() => { if (open) reload(); }, [open, reload]);
  const del = async (p) => {
    if (!window.confirm("Eliminar esta foto? Será retirada dos posts que a usam.")) return;
    try { await api.delete(`/marketing/photos/${p.id}`); reload(); onChanged?.(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="mk-photo-library">
        <DialogHeader>
          <DialogTitle>Fotos das obras</DialogTitle>
          <DialogDescription>Carregue fotos reais. Depois escolha-as em cada publicação ou story.</DialogDescription>
        </DialogHeader>
        <PhotoUploadButton onUploaded={() => { reload(); onChanged?.(); }} variant="default" />
        <PhotoGrid photos={photos} onDelete={del} />
      </DialogContent>
    </Dialog>
  );
}
